import type { ProjectHealthSnapshot } from "../types/projectHealth";

interface ChecklistApiResponse {
  ok?: boolean;
  checklist?: ProjectHealthSnapshot;
  updatedAt?: string;
  briefPdfAvailable?: boolean;
  error?: string;
}

interface AgentChecklistResponse {
  checklist?: ProjectHealthSnapshot;
  updated?: boolean;
  updatedAt?: string;
  briefPdfAvailable?: boolean;
  error?: string;
}

function normalizeSnapshot(response: { checklist?: ProjectHealthSnapshot; updatedAt?: string; briefPdfAvailable?: boolean }) {
  const checklist = response.checklist;
  if (!checklist) return null;

  return {
    ...checklist,
    updatedAt: response.updatedAt ?? checklist.updatedAt,
    briefPdfAvailable: response.briefPdfAvailable ?? checklist.briefPdfAvailable,
  } as ProjectHealthSnapshot;
}

export async function fetchProjectChecklist(projectId: string): Promise<ProjectHealthSnapshot | null> {
  const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/checklist`);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to load project checklist (status ${response.status})`);
  }

  const payload = (await response.json()) as ChecklistApiResponse;

  if (!payload.ok) {
    throw new Error(payload.error ?? "Unknown error while loading project checklist");
  }

  const snapshot = normalizeSnapshot(payload);

  if (!snapshot) {
    throw new Error("Checklist response did not include checklist data");
  }

  return snapshot;
}

export async function generateProjectChecklist(projectId: string): Promise<ProjectHealthSnapshot> {
  const response = await fetch(`/api/agent/projects/${encodeURIComponent(projectId)}/checklist`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(`Failed to generate project checklist (status ${response.status})`);
  }

  const payload = (await response.json()) as AgentChecklistResponse;
  const snapshot = normalizeSnapshot(payload);

  if (!snapshot) {
    throw new Error(payload.error ?? "Checklist generation did not return data");
  }

  return snapshot;
}

export async function downloadProjectBriefPdf(projectId: string): Promise<Blob> {
  const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/project-brief.pdf`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Project brief is not available yet");
    }
    throw new Error(`Failed to download project brief (status ${response.status})`);
  }

  return response.blob();
}
